/**
 * Query Parameter Security Utilities
 *
 * Provides detection and monitoring for suspicious or malicious query parameters.
 * Uses a whitelist approach - any parameter not in ALLOWED_QUERY_PARAMETERS is flagged.
 * Integrates with Sentry for security event tracking.
 *
 * The allowed parameters are derived from the centralized schema in:
 * src/shared/utils/search-params-schema.ts
 */

import * as Sentry from "@sentry/react";
import { ALLOWED_QUERY_PARAMETERS } from "./search-params-schema";

/**
 * Additional patterns to detect particularly dangerous payloads
 * These provide extra context in logs even for parameters in the whitelist
 */
const DANGEROUS_PATTERNS = {
  // Common fuzzing/scanner markers
  fuzzing: /sssied|xss|xxe|sqlmap|acunetix|netsparker|burp|nikto/i,

  // Script injection attempts
  scriptInjection: /<script|javascript:|onerror=|onload=|onclick=/i,

  // SQL injection patterns
  sqlInjection: /union\s+select|drop\s+table|insert\s+into|delete\s+from/i,

  // Path traversal
  pathTraversal: /\.\.\//,

  // Unusually long parameter values (potential buffer overflow attempts)
  longValue: 1000,
} as const;

interface SecurityAnalysis {
  isSuspicious: boolean;
  reasons: string[];
  severity: "low" | "medium" | "high";
  shouldLog: boolean;
  parameterCount: number;
  allowedParamCount: number;
  unknownParamCount: number;
  suspiciousParams: Array<{
    name: string;
    value: string;
    reason: string;
  }>;
}

/**
 * Analyzes query parameters using a whitelist approach.
 * Any parameter not in ALLOWED_QUERY_PARAMETERS is flagged as suspicious.
 */
export function analyzeQueryParameters(searchParams: URLSearchParams): SecurityAnalysis {
  const reasons: string[] = [];
  const suspiciousParams: SecurityAnalysis["suspiciousParams"] = [];
  let severity: SecurityAnalysis["severity"] = "low";

  const paramEntries = Array.from(searchParams.entries());
  const parameterCount = paramEntries.length;

  // Count allowed vs unknown parameters
  let allowedParamCount = 0;
  let unknownParamCount = 0;

  // Analyze each parameter
  for (const [name, value] of paramEntries) {
    const paramReasons: string[] = [];

    // Primary check: Is this parameter in the whitelist?
    const isAllowed = ALLOWED_QUERY_PARAMETERS.includes(
      name as (typeof ALLOWED_QUERY_PARAMETERS)[number],
    );

    if (!isAllowed) {
      unknownParamCount++;
      paramReasons.push("Unknown parameter (not in whitelist)");
      // Unknown parameters are at least medium severity
      severity = severity === "low" ? "medium" : severity;
    } else {
      allowedParamCount++;
    }

    // Additional checks for dangerous patterns in values (even for allowed params)
    // This helps detect injection attempts using legitimate parameter names

    // Check for fuzzing patterns in values
    if (DANGEROUS_PATTERNS.fuzzing.test(value)) {
      paramReasons.push("Fuzzing marker detected");
      severity = "high";
    }

    // Check for script injection
    if (
      DANGEROUS_PATTERNS.scriptInjection.test(value) ||
      DANGEROUS_PATTERNS.scriptInjection.test(name)
    ) {
      paramReasons.push("Script injection pattern");
      severity = "high";
    }

    // Check for SQL injection
    if (DANGEROUS_PATTERNS.sqlInjection.test(value)) {
      paramReasons.push("SQL injection pattern");
      severity = "high";
    }

    // Check for path traversal
    if (DANGEROUS_PATTERNS.pathTraversal.test(value)) {
      paramReasons.push("Path traversal pattern");
      severity = severity === "low" ? "medium" : severity;
    }

    // Check for excessively long values
    if (value.length > DANGEROUS_PATTERNS.longValue) {
      paramReasons.push(`Excessive length: ${value.length} chars`);
      severity = severity === "low" ? "medium" : severity;
    }

    if (paramReasons.length > 0) {
      suspiciousParams.push({
        name,
        value: value.substring(0, 100), // Truncate for logging
        reason: paramReasons.join(", "),
      });
      reasons.push(`Parameter "${name}": ${paramReasons.join(", ")}`);
    }
  }

  // Add summary if unknown parameters were found
  if (unknownParamCount > 0) {
    reasons.unshift(
      `${unknownParamCount} unknown parameter(s) detected (allowed: ${allowedParamCount}, total: ${parameterCount})`,
    );
    // Multiple unknown parameters increases severity
    if (unknownParamCount >= 5) {
      severity = "high";
    }
  }

  const isSuspicious = reasons.length > 0;
  const shouldLog = isSuspicious && (severity === "high" || severity === "medium");

  return {
    isSuspicious,
    reasons,
    severity,
    shouldLog,
    parameterCount,
    allowedParamCount,
    unknownParamCount,
    suspiciousParams,
  };
}

/**
 * Re-export the whitelist for reference (e.g., in tests)
 */
export { ALLOWED_QUERY_PARAMETERS };

/**
 * Logs suspicious query parameter activity to Sentry
 */
function logSuspiciousQueryParameters(
  analysis: SecurityAnalysis,
  pathname: string,
  fullUrl: string,
): void {
  if (!analysis.shouldLog) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag("security_event", "suspicious_query_parameters");
    scope.setTag("severity", analysis.severity);
    scope.setLevel(analysis.severity === "high" ? "warning" : "info");

    scope.setContext("query_analysis", {
      pathname,
      totalParameters: analysis.parameterCount,
      allowedParameters: analysis.allowedParamCount,
      unknownParameters: analysis.unknownParamCount,
      reasons: analysis.reasons,
      severity: analysis.severity,
      timestamp: new Date().toISOString(),
    });

    scope.setContext("suspicious_parameters", {
      count: analysis.suspiciousParams.length,
      parameters: analysis.suspiciousParams,
    });

    scope.setContext("request_info", {
      url: fullUrl,
      userAgent: navigator.userAgent,
      referrer: document.referrer,
    });

    const message = `Suspicious query parameters detected: ${analysis.reasons[0] || "Unknown threat"}`;
    Sentry.captureMessage(message, analysis.severity === "high" ? "warning" : "info");
  });
}

/**
 * Hook to monitor query parameters for security threats
 * Should be called early in the application lifecycle
 */
export function monitorQueryParameters(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const searchParams = new URLSearchParams(window.location.search);
    const analysis = analyzeQueryParameters(searchParams);

    if (analysis.isSuspicious) {
      console.warn("[Security] Suspicious query parameters detected:", {
        severity: analysis.severity,
        count: analysis.suspiciousParams.length,
        reasons: analysis.reasons,
      });

      logSuspiciousQueryParameters(analysis, window.location.pathname, window.location.href);
    }
  } catch (error) {
    // Don't let security monitoring break the app
    console.error("[Security] Error analyzing query parameters:", error);
  }
}
